import React from 'react';
import {
  ShieldCheckIcon,
  ShieldExclamationIcon,
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  SparklesIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import {
  ShieldCheckIcon as ShieldCheckSolid,
  ExclamationTriangleIcon as ExclamationTriangleSolid
} from '@heroicons/react/24/solid';

const EnhancedHealthGauge = ({ score = 0, previousScore = 0, loading = false }) => {
  const change = score - previousScore;
  const changePercent = previousScore > 0 ? ((change / previousScore) * 100) : 0;

  // Determine color based on score
  const getColor = (value) => {
    if (value >= 80) return 'green';
    if (value >= 60) return 'yellow';
    return 'red';
  };

  const color = getColor(score);

  const colorClasses = {
    green: {
      bg: 'bg-green-100',
      text: 'text-green-600',
      border: 'border-green-200',
      ring: 'ring-green-500'
    },
    yellow: {
      bg: 'bg-yellow-100',
      text: 'text-yellow-600',
      border: 'border-yellow-200',
      ring: 'ring-yellow-500'
    },
    red: {
      bg: 'bg-red-100',
      text: 'text-red-600',
      border: 'border-red-200',
      ring: 'ring-red-500'
    }
  };

  const classes = colorClasses[color];

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 h-full flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <div className="h-6 bg-gray-200 rounded w-32 animate-pulse"></div>
          <div className="w-10 h-10 bg-gray-200 rounded-lg animate-pulse"></div>
        </div>

        {/* Large Score Display Skeleton */}
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <div className="h-14 bg-gray-200 rounded w-24 mb-2 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
          </div>
        </div>

        {/* Progress Bar Skeleton */}
        <div className="relative pt-1 flex-1 flex flex-col justify-center">
          <div className="h-3 bg-gray-200 rounded w-24 mb-2 animate-pulse"></div>
          <div className="h-3 bg-gray-200 rounded-full animate-pulse"></div>
          <div className="flex justify-between mt-1">
            <div className="h-3 bg-gray-200 rounded w-6 animate-pulse"></div>
            <div className="h-3 bg-gray-200 rounded w-6 animate-pulse"></div>
            <div className="h-3 bg-gray-200 rounded w-8 animate-pulse"></div>
          </div>
        </div>

        {/* Status Text Skeleton */}
        <div className="mt-4 p-3 rounded-lg bg-gray-100 border border-gray-200">
          <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Overall Health</h3>
        <div className={`p-2 rounded-lg ${classes.bg}`}>
          {score >= 80 ? (
            <ShieldCheckSolid className={`h-6 w-6 ${classes.text}`} />
          ) : (
            <ExclamationTriangleSolid className={`h-6 w-6 ${classes.text}`} />
          )}
        </div>
      </div>

      {/* Large Score Display */}
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className={`text-5xl font-bold ${classes.text}`}>
            {loading ? '–' : `${Math.round(score)}%`}
          </div>
          <p className="text-sm text-gray-600 mt-1">Data Quality Score</p>
        </div>

        {/* Week-over-Week Change */}
        {!loading && change !== 0 && (
          <div className="text-right">
            <div className={`flex items-center gap-1 text-sm font-semibold ${
              change > 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {change > 0 ? (
                <ArrowTrendingUpIcon className="h-4 w-4" />
              ) : (
                <ArrowTrendingDownIcon className="h-4 w-4" />
              )}
              <span>
                {Math.abs(change).toFixed(1)} pts
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">vs last week</p>
          </div>
        )}
      </div>

      {/* Score Range Indicator */}
      <div className="relative pt-1 flex-1 flex flex-col justify-center">
        <div className="flex mb-2 items-center justify-between">
          <div className="text-xs font-semibold text-gray-600">Score Range</div>
        </div>
        <div className="overflow-hidden h-3 text-xs flex rounded-full bg-gray-200">
          <div
            style={{ width: `${Math.max(0, Math.min(100, score))}%` }}
            className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center ${
              score >= 80 ? 'bg-green-500' :
              score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
            } transition-all duration-500`}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>0</span>
          <span>50</span>
          <span>100</span>
        </div>
      </div>

      {/* Status Text. Icon picks up the same color as the surrounding
          text via Tailwind's currentColor inheritance on stroke. */}
      <div className={`mt-4 p-3 rounded-lg ${classes.bg} ${classes.border} border`}>
        <p className={`text-sm font-medium ${classes.text} flex items-center`}>
          {(() => {
            if (score >= 90) {
              return (
                <>
                  <SparklesIcon className="h-4 w-4 mr-1.5 flex-shrink-0" />
                  Excellent data quality!
                </>
              );
            }
            if (score >= 80) {
              return (
                <>
                  <CheckCircleIcon className="h-4 w-4 mr-1.5 flex-shrink-0" />
                  Good data quality
                </>
              );
            }
            if (score >= 60) {
              return (
                <>
                  <ExclamationTriangleIcon className="h-4 w-4 mr-1.5 flex-shrink-0" />
                  Some issues detected
                </>
              );
            }
            return (
              <>
                <ShieldExclamationIcon className="h-4 w-4 mr-1.5 flex-shrink-0" />
                Critical issues require attention
              </>
            );
          })()}
        </p>
      </div>
    </div>
  );
};

export default EnhancedHealthGauge;
